import { useEffect, useMemo, useState } from 'react';
import { targetsSettingsSchema, type AppSettings, type TargetsSettings } from '@ace-tracker/shared';
import { Button } from '../Button';
import { AmountInput, Card, Field, IconButton, SaveBar, SelectInput, amountToString, stringToAmount } from './formParts';
import { useSaveSettings } from './useSaveSettings';

const GOAL_KEYS = [
  'monthlyBudget',
  'cacheHitGoalClaudeCode',
  'cacheHitGoalCodex',
  'outputPerCostGoalClaudeCode',
  'outputPerCostGoalCodex',
  'frontierShareMaxPercent',
  'costPerDeveloperMax',
] as const;
type GoalKey = (typeof GOAL_KEYS)[number];

type Draft = Record<GoalKey, string> & { teamBudgets: { key: number; team: string; amount: string }[] };

let nextKey = 1;

function toDraft(t: TargetsSettings): Draft {
  const goals = Object.fromEntries(GOAL_KEYS.map((k) => [k, amountToString(t[k])])) as Record<GoalKey, string>;
  return { ...goals, teamBudgets: t.teamBudgets.map((b) => ({ key: nextKey++, team: b.team, amount: amountToString(b.monthlyBudget) })) };
}

function toTargets(d: Draft) {
  return {
    ...Object.fromEntries(GOAL_KEYS.map((k) => [k, stringToAmount(d[k])])),
    teamBudgets: d.teamBudgets.map((b) => ({ team: b.team, monthlyBudget: b.amount.trim() === '' ? NaN : Number(b.amount) })),
  };
}

const FIELD_LABELS: Record<string, string> = {
  monthlyBudget: 'Monthly budget',
  cacheHitGoalClaudeCode: 'Claude Code cache hit goal',
  cacheHitGoalCodex: 'Codex cache hit goal',
  outputPerCostGoalClaudeCode: 'Claude Code output per cost goal',
  outputPerCostGoalCodex: 'Codex output per cost goal',
  frontierShareMaxPercent: 'Frontier share goal',
  costPerDeveloperMax: 'Cost per developer goal',
  team: 'Team',
};

export function TargetsSettingsForm({ settings }: { settings: AppSettings }) {
  const baseline = settings.targets;
  const cur = settings.cost.currency;
  const [draft, setDraft] = useState<Draft>(() => toDraft(baseline));
  const [validationError, setValidationError] = useState<string>();
  const { save, saving, error, savedAt, reset } = useSaveSettings('targets');
  const baselineJson = useMemo(() => JSON.stringify(toTargets(toDraft(baseline))), [baseline]);

  useEffect(() => setDraft(toDraft(baseline)), [baseline]);

  const dirty = JSON.stringify(toTargets(draft)) !== baselineJson;
  const unbudgetedTeams = settings.teams.filter((team) => !draft.teamBudgets.some((b) => b.team.toLowerCase() === team.toLowerCase()));

  function set(patch: Partial<Draft>) {
    setValidationError(undefined);
    setDraft((d) => ({ ...d, ...patch }));
  }

  function handleSave() {
    const parsed = targetsSettingsSchema.safeParse(toTargets(draft));
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      const field = FIELD_LABELS[String(issue.path[issue.path.length - 1])] ?? (issue.path[0] === 'teamBudgets' ? 'Team budget' : 'Value');
      setValidationError(`${field}: ${issue.message.startsWith('Expected') ? 'enter a number' : issue.message}`);
      return;
    }
    save(parsed.data);
  }

  const goalInput = (key: GoalKey, label: string, hint: string) => (
    <Field label={label} hint={hint} htmlFor={`target-${key}`}>
      <AmountInput id={`target-${key}`} label={label} allowEmpty placeholder="Not set" className="max-w-[10rem]" value={draft[key]} onChange={(v) => set({ [key]: v })} />
    </Field>
  );

  return (
    <div className="space-y-5">
      <Card
        title="Monthly budget"
        description={`Claude Code and Codex indicative cost per calendar month (UTC), in ${cur}. Shown on the Spend view with a month-end projection. Display only: no alerts are sent.`}
      >
        <div className="max-w-xs">{goalInput('monthlyBudget', `Organization budget (${cur})`, 'Leave empty for no budget.')}</div>

        <h4 className="mt-6 text-xs font-medium uppercase tracking-wide text-text-faint">Team budgets</h4>
        <p className="mt-1 text-xs text-text-muted">
          A team's figures are shown only when it has at least the minimum team size (Settings → Executive) of active developers, so no
          individual's spend can be singled out.
        </p>
        <ul className="mt-3 space-y-2">
          {draft.teamBudgets.map((b) => (
            <li key={b.key} className="flex items-center gap-3">
              <span className="min-w-[12rem] text-sm text-text-default">{b.team}</span>
              <AmountInput
                label={`Monthly budget for ${b.team} (${cur})`}
                className="max-w-[10rem]"
                value={b.amount}
                onChange={(v) => set({ teamBudgets: draft.teamBudgets.map((x) => (x.key === b.key ? { ...x, amount: v } : x)) })}
              />
              <IconButton
                icon="fa-solid fa-trash-can"
                label={`Remove the budget for ${b.team}`}
                onClick={() => set({ teamBudgets: draft.teamBudgets.filter((x) => x.key !== b.key) })}
              />
            </li>
          ))}
        </ul>
        {unbudgetedTeams.length > 0 ? (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <label htmlFor="target-add-team" className="sr-only">
              Team
            </label>
            <SelectInput
              id="target-add-team"
              className="max-w-[16rem]"
              value=""
              onChange={(e) => e.target.value && set({ teamBudgets: [...draft.teamBudgets, { key: nextKey++, team: e.target.value, amount: '' }] })}
            >
              <option value="">Add a team budget…</option>
              {unbudgetedTeams.map((team) => (
                <option key={team} value={team}>
                  {team}
                </option>
              ))}
            </SelectInput>
          </div>
        ) : (
          settings.teams.length === 0 && <p className="mt-3 text-xs text-text-muted">Create teams in Settings → Teams to set team budgets.</p>
        )}
      </Card>

      <Card
        title="Efficiency goals"
        description="Drawn as goal lines on the Efficiency view trends and marked met or not on its summary cards. Each tool is measured in its own terms, so each has its own goal."
      >
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {goalInput('cacheHitGoalClaudeCode', 'Claude Code cache hit ratio (%)', 'At least this. 0–100.')}
          {goalInput('cacheHitGoalCodex', 'Codex cache hit ratio (%)', 'At least this. 0–100.')}
          {goalInput('outputPerCostGoalClaudeCode', `Claude Code output tokens per ${cur}`, 'At least this.')}
          {goalInput('outputPerCostGoalCodex', `Codex output tokens per ${cur}`, 'At least this.')}
        </div>
      </Card>

      <Card title="Spend goals" description="Shown on the Spend view: the frontier share on the model tier mix, and cost per developer next to the budget.">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {goalInput('frontierShareMaxPercent', 'Frontier models, share of cost (%)', 'At most this. 0–100.')}
          {goalInput('costPerDeveloperMax', `Cost per active developer per month (${cur})`, 'At most this. An organization average, never per person.')}
        </div>
      </Card>

      <div className="flex">
        <Button
          type="button"
          variant="text"
          onClick={() => set({ ...(Object.fromEntries(GOAL_KEYS.map((k) => [k, ''])) as Record<GoalKey, string>), teamBudgets: [] })}
        >
          Clear all budgets and goals
        </Button>
      </div>

      <SaveBar
        dirty={dirty}
        saving={saving}
        error={validationError ?? error}
        savedAt={savedAt}
        audit={settings.audit.targets}
        onSave={handleSave}
        onDiscard={() => {
          setDraft(toDraft(baseline));
          setValidationError(undefined);
          reset();
        }}
      />
    </div>
  );
}
